/**
 * Entrar por outra porta encerra a sessão em curso.
 *
 * Sem isso, quem estivesse logado na marca A e abrisse o endereço da marca B
 * veria a identidade de B com a sessão de A por baixo — e, dependendo da
 * tela, dado de A. A sessão pertence à porta (A027); trocar de porta é
 * recomeçar.
 *
 * A comparação espera a marca resolver porque o endereço traz um SLUG e a
 * sessão traz um ID. Resolver o slug é justamente o que o `BrandProvider`
 * faz; duplicar isso aqui seria uma segunda chamada e uma segunda verdade.
 *
 * Vale só para endereços que significam "quero entrar" — subdomínio e
 * `/entrar`. `/convite/<token>` e `/cadastro/*` ficam de fora: são páginas
 * públicas com marca própria, e quem abre um convite de outra marca não pediu
 * para trocar de nada.
 */
import { useEffect } from 'react';
import { sameBrand } from '@eva/schemas';
import { useBrand, useSession } from '@eva/domain';
import { limparSessaoDoAdmin } from '../portals/marca/shims/AuthProvider';

const PORTAS = new Set(['subdomain', 'path']);

export function GuardaDePorta() {
  const { brand, detection, isLoading } = useBrand();
  const session = useSession();

  useEffect(() => {
    if (isLoading) return;
    if (!PORTAS.has(detection.source)) return;
    if (session.status !== 'ready' && session.status !== 'choosing') return;

    const daSessao = session.status === 'ready' ? session.context?.brandId : null;
    // Em `choosing` ainda não há contexto: o próprio login já filtrou pela
    // porta, então não há o que conferir.
    if (!daSessao) return;

    if (!sameBrand(daSessao, brand.id)) {
      /*
       * O espelho do painel sai junto.
       *
       * `session.logout()` limpa as chaves do app, mas o `/marca` mantém uma
       * cópia dos tokens em `eva_admin_*` para o cliente HTTP do painel legado
       * (A019). Sem apagá-la aqui, sobrariam credenciais da marca anterior no
       * navegador depois de uma troca de porta — exatamente o que a troca
       * existe para evitar.
       */
      limparSessaoDoAdmin();
      void session.logout();
    }
  }, [brand.id, detection.source, isLoading, session]);

  return null;
}
